/**
 * V2 Session Helper — access to the PalCare app session, encrypted at rest.
 *
 * The session (dData) is held in memory for synchronous reads and mirrored to
 * sessionStorage under "appSession" as an AES-GCM encrypted, tamper-evident
 * blob. The key is derived per-user from the authenticated Cognito identity
 * (idToken `sub`) plus a build constant, so the stored value is neither
 * human-readable nor casually editable, and a hand-edit fails the GCM auth tag
 * on read — which we treat as tampering: drop the session and force re-login.
 *
 * SECURITY NOTE: this is defense-in-depth, not a trust boundary. Anything the
 * browser can decrypt, a determined local user can too (the key derives from
 * data also present in the browser). Authoritative authorization MUST happen
 * server-side against the JWT identity — never trust role_weight / campus read
 * back out of this blob.
 *
 * initSession() must run (and be awaited) once at startup before any
 * synchronous getSession() call — index.jsx gates the first render on it.
 * Auth tokens themselves are managed separately by AWS Amplify.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const STORAGE_KEY = 'appSession';

// Not a real secret (client bundles are readable) — it only ensures the derived
// key isn't a bare function of the public `sub`. Bump the suffix to force all
// existing stored blobs to be treated as stale (they'll fail to decrypt).
const KEY_PEPPER = 'palcare.portal.session.v1';

// In-memory session (source of truth for synchronous reads) and the derived
// AES-GCM key. Both are rebuilt on every page load via initSession().
let _cache = null;
let _cryptoKey = null;
let _cryptoKeySub = null; // the Cognito sub the cached key was derived from

// ---------------------------------------------------------------------------
// Crypto helpers (WebCrypto — no external dependency)
// ---------------------------------------------------------------------------

const _enc = new TextEncoder();
const _dec = new TextDecoder();

const _delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function _toB64(bytes) {
  const arr = new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < arr.length; i++) s += String.fromCharCode(arr[i]);
  return btoa(s);
}

function _fromB64(b64) {
  const s = atob(b64);
  const arr = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) arr[i] = s.charCodeAt(i);
  return arr;
}

/** Cognito subject (stable per user, survives token refresh), or null. */
async function _getSub() {
  try {
    const s = await fetchAuthSession();
    return s?.tokens?.idToken?.payload?.sub ?? null;
  } catch {
    return null;
  }
}

/**
 * Derive the per-user AES-GCM key from the CURRENT Cognito sub, re-deriving if
 * the authenticated identity changed since the key was last cached.
 *
 * Binding the cache to the sub is essential: the login flow (signOut → signIn)
 * changes identity, and a key cached at page-load from a leftover/previous
 * Amplify token must not be reused to encrypt the new user's session — doing so
 * makes the post-reload decrypt fail its GCM tag (OperationError) and bounces
 * the user back to login.
 */
async function _ensureKey() {
  const sub = await _getSub();
  if (!sub) return null;
  if (_cryptoKey && _cryptoKeySub === sub) return _cryptoKey;
  const hash = await crypto.subtle.digest('SHA-256', _enc.encode(`${KEY_PEPPER}:${sub}`));
  _cryptoKey = await crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  _cryptoKeySub = sub;
  return _cryptoKey;
}

async function _encrypt(obj, key) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, _enc.encode(JSON.stringify(obj)));
  return `${_toB64(iv)}.${_toB64(ct)}`;
}

async function _decrypt(raw, key) {
  const [ivB64, ctB64] = String(raw).split('.');
  if (!ivB64 || !ctB64) throw new Error('malformed session blob');
  // GCM verifies the auth tag here — a tampered blob throws.
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: _fromB64(ivB64) }, key, _fromB64(ctB64));
  return JSON.parse(_dec.decode(pt));
}

/** Encrypt + write the mirror. No-op (returns) until the user is authenticated. */
async function _persist(dData) {
  const key = await _ensureKey();
  if (!key) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, await _encrypt(dData, key));
  } catch (err) {
    console.error('[session] Failed to persist session:', err);
  }
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * One-time migration shim. Before at-rest encryption, the session was stored as
 * plain JSON. When decryption fails we check whether the stored value is such a
 * legacy plaintext session so we can adopt it (and re-persist it encrypted)
 * instead of forcing a re-login on the first load after this feature ships.
 *
 * Returns the parsed legacy session, or null if `raw` isn't a plaintext session.
 * Note: an encrypted blob is `b64.b64`, which is not valid JSON, so a genuine
 * ciphertext that failed to decrypt (e.g. key/pepper rotation) is NOT adopted
 * here — it correctly falls through to the clear-and-relogin path.
 *
 * TODO: remove this shim once all active sessions have rolled over (the window
 * is bounded by sessionStorage lifetime — a browser/tab restart clears it).
 */
function _tryLegacyPlaintext(raw) {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  // Shape check: a real PalCare session is marked with slauth + a tKey.
  if (parsed && typeof parsed === 'object' && parsed.slauth === true && parsed.tKey) {
    return parsed;
  }
  return null;
}

/**
 * Decrypt the stored session into memory. Call once at startup and AWAIT it
 * before rendering anything that reads the session. On a tamper/key mismatch
 * the stored blob is dropped so the app falls back to the login screen.
 */
export async function initSession() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    raw = null;
  }
  let key = await _ensureKey();
  // If a stored session exists but we couldn't derive a key, the Amplify auth
  // session may simply not be hydrated yet on this reload. Retry briefly before
  // discarding a possibly-valid session, so an authenticated user isn't bounced
  // to login by a transient null identity. Bounded (≤5 × 150ms) so startup can't
  // hang; only incurred when a blob is present but identity is momentarily
  // unavailable (a genuinely logged-out user has no blob and skips this).
  if (raw && !key) {
    for (let attempt = 0; attempt < 5 && !key; attempt++) {
      await _delay(150);
      key = await _ensureKey();
    }
  }
  if (!key || !raw) {
    _cache = null;
    return;
  }
  try {
    _cache = await _decrypt(raw, key);
  } catch (err) {
    // Legacy migration: adopt a pre-encryption plaintext session once, then
    // upgrade it to the encrypted format so subsequent loads are protected.
    const legacy = _tryLegacyPlaintext(raw);
    if (legacy) {
      console.info('[session] Migrating legacy plaintext session to encrypted storage.');
      _cache = legacy;
      await _persist(legacy);
      return;
    }
    console.warn('[session] Session unreadable (tampered or stale) — clearing.', err);
    _cache = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}

// ---------------------------------------------------------------------------
// Core accessor
// ---------------------------------------------------------------------------

/**
 * Returns the full session object (dData), or null if not logged in.
 * Reads the in-memory copy populated by initSession()/setSession().
 *
 * @returns {object | null}
 */
export function getSession() {
  // If the encrypted blob was cleared out-of-band (logout, tokenRefresh
  // failure → sessionStorage.clear()), drop the in-memory copy too so callers
  // observe a logged-out state without needing a reload.
  try {
    if (!sessionStorage.getItem(STORAGE_KEY)) {
      _cache = null;
      return null;
    }
  } catch {
    /* sessionStorage unavailable — fall back to the in-memory copy */
  }
  // Deep copy so callers mutating the result don't corrupt the cache until they
  // explicitly setSession() (preserves the old JSON.parse-per-read semantics).
  return _cache ? JSON.parse(JSON.stringify(_cache)) : null;
}

/**
 * Updates the session. The in-memory copy is set synchronously (so an immediate
 * getSession() reflects it); the encrypted sessionStorage mirror is written
 * asynchronously. Returns the persist promise so callers that reload right
 * after (login) can await it; in-app callers can safely ignore it.
 *
 * @param {object} dData — The full session object to persist
 * @returns {Promise<void>}
 */
export function setSession(dData) {
  _cache = dData;
  // sessionStorage doesn't fire `storage` events in the same tab, so anything
  // that needs to react to mid-session changes (e.g. portal_afr toggling) has
  // to listen for this custom event instead.
  window.dispatchEvent(new CustomEvent('session:updated'));
  return _persist(dData);
}

// ---------------------------------------------------------------------------
// Convenience accessors
// ---------------------------------------------------------------------------

/** Current campus ID */
export function getCampusId() {
  return getSession()?.acnt ?? null;
}

/** My campus ID */
export function getMyCampusId(){
  return getSession()?.myAcnt ?? null;
}

/** Current user ID */
export function getUserId() {
  return getSession()?.user ?? null;
}

/** Current session ID (used as ClientId in API calls) */
export function getSessionId() {
  return getSession()?.sessionId ?? null;
}

/** User's last-acknowledged portal version (X.Y.Z), or null if never set */
export function getPortalVersion() {
  return getSession()?.portal_version ?? null;
}

/**
 * Campus-specific configuration ("campus config") for a given campus.
 *
 * Pass a `campusId` to target a specific campus (e.g. pages with a campus
 * selector); omit it to use the current active campus. Returns null if not found.
 *
 * Canonical V2 usage inside a component — every page that needs campus config
 * should derive it this one way and read fields off the result:
 *
 *   const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
 *   ... campusConfig?.event_notes_active ...
 */
export function getCampusConfig(campusId) {
  const s = getSession();
  if (!s?.campusData) return null;
  const id = campusId ?? s?.acnt;
  if (id == null) return null;
  return s.campusData[id.toString()] ?? null;
}

/** Campus name */
export function getCampusName() {
  return getSession()?.campus ?? '';
}

/**
 * Full campus list for the campus selector dropdown.
 *
 * dData.acntList is an array of plain campus IDs (e.g. [1, 5, 12]).
 * Campus names/config live in dData.campusData[id]. This function
 * transforms the raw IDs into objects the V2 UI expects.
 *
 * @returns {Array<{campus_id: number, campus_name: string, valid: boolean}>}
 */
export function getCampusList() {
  const s = getSession();
  if (!s?.acntList || !s?.campusData) return [];
  return s.acntList
    .map((id) => ({
      campus_id: id,
      campus_name: s.campusData[id.toString()]?.name || `Campus ${id}`,
      valid: s.campusData[id.toString()]?.valid ?? true,
    }))
    // Alphabetical by name so every V2 dropdown matches the classic portal's order.
    .sort((a, b) => a.campus_name.localeCompare(b.campus_name, undefined, { numeric: true, sensitivity: 'base' }));
}

/**
 * Persist a campus change to the session.
 * Mirrors V1's setACNT() — updates dData.acnt and copies
 * campus-specific feature flags to the root session object.
 *
 * @param {number} campusId — The new campus ID to persist
 */
export function updateSessionCampus(campusId) {
  const s = getSession();
  if (!s) return;

  const id = campusId.toString();
  const cfg = s.campusData?.[id] || {};

  s.acnt = campusId;
  s.campus = cfg.name || s.campus;
  s.rounds = cfg.is_rounding || 0;
  s.checkin = cfg.is_resident_checkin || 0;
  s.evmSiteId = cfg.location_site_id || '0';
  s.cloud = cfg.is_cloud || 0;
  s.hybrid = cfg.is_hybrid || false;
  s.legacy = cfg.show_legacy || false;
  s.new = cfg.show_new || false;
  s.server_version = cfg.server_version || '';

  setSession(s);
}

/**
 * Effective user role weight (for permission checks).
 *
 * When impersonating, `dData.role_weight` reflects the impersonated role
 * (e.g. 10 for Caregiver), while `dData.my_role_weight` remains the
 * original login role (e.g. 1000 for PalCare Admin). Authorization
 * checks must respect the impersonated role — otherwise
 * `iAmHigherAuthority`'s `>= 1000` short-circuit lets the impersonator
 * bypass every role-weight gate. Fall back to `my_role_weight` only if
 * the session was written before impersonation was recorded.
 */
export function getRoleWeight() {
  const s = getSession();
  return s?.role_weight ?? 10;
}

/**
 * Alert sound config for 5.0 campuses: manufacture_id -> device_type_id -> file
 * name, from sp_getPalCareSounds via the session endpoint.
 *
 * Keys are strings (JSON serialization of the procedure's int columns) — resolve
 * lookups through src/sound/soundMap.js rather than indexing this directly.
 * Empty object when a campus/session predates the field.
 */
export function getAlertSounds() {
  const s = getSession();
  return s?.sounds || {};
}

export function getMyRoleWeight(){
  const s = getSession();
  return s?.my_role_weight ?? 10;
}

/** Whether user has at least the given role weight */
export function hasMinRole(minWeight) {
  return getRoleWeight() >= minWeight;
}

/**
 * Converts a campus ID to its GUID for DynamoDB operations.
 * Wraps the V1 `campusToGuid(dData, campusId)` pattern from libs.jsx.
 *
 * @param {number|string} [campusId] — Campus ID to convert, defaults to current campus
 * @returns {string} Campus GUID, or the campus ID as fallback
 */
export function getCampusGuid(campusId) {
  const s = getSession();
  if (!s) return campusId ?? '';
  const id = (campusId ?? s.acnt)?.toString();
  return s.campusData?.[id]?.guid || id;
}

/**
 * Returns the current user's username (for ModifiedBy fields).
 */
export function getUsername() {
  return getSession()?.username ?? '';
}

/**
 * Returns an array of vendor_ids for every active vendor on a campus (current
 * campus by default). Used to populate the `vendor_ids` field on transaction
 * payloads (replaces the old offsite/offsite_id pair).
 */
export function getActiveVendorIdList(campusId){
  return getAllVendors(campusId)
    .filter((v) => v?.vendor_active)
    .map((v) => v?.vendor_id)
    .filter((id) => id != null);
}

// ---------------------------------------------------------------------------
// Third-party vendor selectors
//
// Each campus config carries a `vendors` array (see getMyCorpCampusDetails on
// the backend). Vendor shape:
//   { vendor_id, vendor_type_id, vendor_name, vendor_active,
//     is_receiving, is_receiving_user, stg_count }
// vendor_type_id: 1 = EHR, 2 = Software, 3 = Offsite Monitoring.
//
// All selectors default to the current campus (dData.acnt); pass a campusId to
// target a specific campus.
// ---------------------------------------------------------------------------

/** All vendors for a campus (or current campus when campusId is omitted). */
export function getAllVendors(campusId) {
  const s = getSession();
  if (!s?.campusData) return [];
  const cid = campusId ?? s.acnt;
  if (cid == null) return [];
  const cfg = s.campusData[cid.toString()];
  return Array.isArray(cfg?.vendors) ? cfg.vendors : [];
}

/** EHR vendors (vendor_type_id === 1). */
export function getEHRVendors(campusId) {
  return getAllVendors(campusId).filter((v) => v?.vendor_type_id === 1);
}

/** Software vendors (vendor_type_id === 2). */
export function getSoftwareVendors(campusId) {
  return getAllVendors(campusId).filter((v) => v?.vendor_type_id === 2);
}

/** Offsite monitoring vendors (vendor_type_id === 3). */
export function getOffsiteMonitoringVendors(campusId) {
  return getAllVendors(campusId).filter((v) => v?.vendor_type_id === 3);
}

/**
 * Primary EHR vendor info for a campus (current campus by default), mapped to
 * the flat shape the app consumes. Replaces the old per-campus ehr_* config
 * fields. Prefers an active EHR vendor, else the first configured one.
 *   { active, vendor_id, vendor_name, stg_count,
 *     receiving_resident (is_receiving), receiving_user (is_receiving_user),
 *     resident_locked }
 */
export function getEhrInfo(campusId) {
  const vendors = getEHRVendors(campusId);
  const vendor = vendors.find((v) => v?.vendor_active) || vendors[0] || null;
  return {
    active: !!vendor?.vendor_active,
    vendor_id: vendor?.vendor_id ?? 0,
    vendor_name: vendor?.vendor_name ?? '',
    stg_count: vendor?.stg_count ?? 0,
    receiving_resident: !!vendor?.is_receiving,
    receiving_user: !!vendor?.is_receiving_user,
    /* Campus-wide switch: when the partner sets it, every resident on the campus is
       locked, not just the ones it has already synced. Delivered as a real boolean by
       both session lambdas, so no tinyint coercion is needed here. */
    resident_locked: !!vendor?.resident_locked,
  };
}

/**
 * Is resident + room data owned by an EHR integration on this campus?
 *
 * When true, resident records and room assignments are pushed from the partner
 * system and must not be edited by hand in the portal — a manual change is
 * either overwritten on the next sync or silently diverges from the customer's
 * system of record. Resident **groups** are the exception: they're a PalCare-only
 * concept the EHR knows nothing about, so they stay editable.
 *
 * Requires **both** an active EHR vendor (`vendor_type_id === 1`, today vendor_ids
 * 2, 4, 5, 7, 13 and 16 — a type check rather than a hardcoded list so a new EHR
 * partner is covered the moment it's marked active) **and** that vendor actually
 * receiving residents (`is_receiving`).
 *
 * The receiving half matters: an EHR can be active for users only. On such a
 * campus nothing pushes resident records, no move-in/relocate/move-out approval
 * tasks are ever created, and the EHR tasks page renders no Residents tab at all
 * — so if the portal also refused to edit residents, there would be no way to
 * manage them anywhere. Those campuses therefore use the ordinary portal path.
 *
 * Read through `getEhrInfo` on purpose. TasksEhrPage decides whether the
 * Residents tab and its approval subtabs exist from `getEhrInfo().receiving_resident`,
 * so sharing that accessor makes it impossible for this gate to disagree with
 * whether the approval surface is even there. Note it reports the *primary* EHR
 * vendor (first active, else first configured), so a campus running two EHR
 * vendors is judged on that one rather than on any-of.
 *
 * Its own named helper so call sites read as the rule they're enforcing, and so
 * there is exactly one definition to change if the rule moves. Consumed by the V2
 * residents list and profile gates and by the help guide's `ctx.ehrActive`, which
 * is what routes the lifecycle guides to the EHR task-approval procedure.
 */
export function isResidentDataEhrManaged(campusId) {
  const info = getEhrInfo(campusId);
  return info.active && info.receiving_resident;
}

/**
 * Does this individual resident have an EHR identity?
 *
 * `ehr_resident_id` is the modern field; `ehr_external_id` is its legacy twin on
 * core_resident and is deliberately NOT consulted — the codebase was migrated to
 * the modern name, and treating a stale legacy value as an EHR identity would lock
 * residents the partner does not actually own.
 *
 * Null-safe and whitespace-safe: an id of 0, '', '   ' or null all mean "no EHR
 * identity". Numeric ids are accepted, so String() before trimming.
 */
export function hasEhrResidentId(resident) {
  const raw = resident?.ehr_resident_id;
  if (raw == null) return false;
  const str = String(raw).trim();
  return str !== '' && str !== '0';
}

/**
 * Is THIS resident's data locked from portal editing?
 *
 * Two gates, in order:
 *
 *  1. Campus gate — the EHR vendor must be active AND receiving residents
 *     (isResidentDataEhrManaged). On a campus where nothing pushes resident records
 *     there is no system of record to diverge from, and no EHR approval surface to
 *     manage them through, so the portal stays fully editable.
 *  2. Resident gate — with the campus gate passed, the resident is locked when
 *     EITHER the campus-wide `resident_locked` switch is set OR that specific
 *     resident already has an EHR identity.
 *
 * The second half is why this is per-resident rather than per-campus: on a receiving
 * campus without `resident_locked`, a resident created in the portal and never synced
 * has no EHR identity and stays editable, while one the partner owns does not. The
 * previous rule locked every resident on such a campus indiscriminately.
 *
 * Does NOT consider role. The role-80 override lives at the call sites, which combine
 * this with iAmHigherAuthority so the override reads plainly where rights are built.
 *
 * @param {object} resident — a resident row or profile record
 * @param {number} [campusId]
 */
export function isResidentEditLocked(resident, campusId) {
  if (!isResidentDataEhrManaged(campusId)) return false;
  return getEhrInfo(campusId).resident_locked || hasEhrResidentId(resident);
}

/**
 * Update the staging-task count on the campus's primary EHR vendor and persist
 * the session. Used to live-refresh the EHR badge without a full reload
 * (replaces writing the old ehr_stg_count flat field).
 */
export function setEhrStgCount(campusId, count) {
  const s = getSession();
  if (!s?.campusData) return;
  const cid = campusId ?? s.acnt;
  if (cid == null) return;
  const vendors = s.campusData[cid.toString()]?.vendors;
  if (!Array.isArray(vendors)) return;
  const vendor = vendors.find((v) => v?.vendor_type_id === 1 && v?.vendor_active)
    || vendors.find((v) => v?.vendor_type_id === 1)
    || null;
  if (!vendor) return;
  vendor.stg_count = count;
  setSession(s);
}

/**
 * Set the active state of a specific vendor (by vendor_id) on a campus in the
 * session and persist. Used after toggling a third-party integration so the
 * derived selectors (getEhrInfo, getOffsiteMonitoringVendors, etc.) reflect the change
 * without a re-login. Returns true when the vendor was found and updated.
 */
export function setVendorActive(campusId, vendorId, active) {
  const s = getSession();
  if (!s?.campusData) return false;
  const cid = campusId ?? s.acnt;
  if (cid == null) return false;
  const vendors = s.campusData[cid.toString()]?.vendors;
  if (!Array.isArray(vendors)) return false;
  const vendor = vendors.find((v) => String(v?.vendor_id) === String(vendorId));
  if (!vendor) return false;
  vendor.vendor_active = !!active;
  setSession(s);
  return true;
}

/**
 * Persist the Alerts page view mode (mobile vs. standard) to the session so it
 * survives a refresh within the current session. Read back via
 * getSession()?.alertsMobileView.
 */
export function setAlertsMobileView(enabled) {
  const s = getSession();
  if (!s) return;
  s.alertsMobileView = Boolean(enabled);
  setSession(s);
}
